import { useState } from '@biletiniz/ui-core';
import { useMutation } from '@biletiniz/apollo';
import { CountriesList, range } from '@biletiniz/shared';
import { useI18n } from '@biletiniz/intl';
import {
  BOOK_FLIGHT_OFFER_MUTATION,
  RESERVE_FLIGHT_OFFER_MUTATION,
} from './gql';

const passengerTypeMapper = {
  ADT: 'adult',
  CHD: 'child',
  INF: 'infant',
};

/**
 * The country selects hold a `{ value, label }` option, and submit reads
 * `.value` off it, so an ISO code coming back from the API as a bare string
 * has to be resolved to a full option first — otherwise it silently submits as
 * an empty country. An unresolvable code becomes '' so validation blocks it
 * instead of shipping a blank field.
 */
const toCountryOption = (value, locale) => {
  if (!value) return '';
  if (typeof value === 'object') return value.value ? value : '';
  const country = CountriesList.find(item => item.value === value);
  return country ? { value: country.value, label: country.label[locale] } : '';
};

const mapToBiletinizPassenger = (passenger, type) => ({
  passengerTypeCode: type,
  givenName: passenger.firstName,
  surname: passenger.lastName,
  gender: passenger.gender,
  hesCode: passenger.hesCode,
  passportNo: passenger.passportNo,
  tcdegil: passenger.tcdegil,
  tc: passenger.tc,
  mileNumber: passenger.mileNumber,
  birthDate: `${passenger.dateOfBirth.year}-${passenger.dateOfBirth.month}-${passenger.dateOfBirth.day}`,
  passportExpirationDate: `${passenger.passportExpirationDate.year}-${passenger.passportExpirationDate.month}-${passenger.passportExpirationDate.day}`,
  passportCountry: passenger.passportCountry?.value || '',
  nationality: passenger.nationality?.value || '',
});

/**
 * The date selects are zero padded ('01'..'09'), so a value coming back from the
 * API as `2029-3-16` has to be padded before it can match an option.
 */
const padDatePart = part => {
  const value = String(part ?? '');
  return value.length === 1 ? `0${value}` : value;
};

/**
 * Accepts either an `yyyy-MM-dd` string (how the API stores dates) or an
 * already split `{ day, month, year }` object (how the form holds them) and
 * normalizes it to the shape the date selects expect.
 */
const toDateParts = value => {
  let day = '';
  let month = '';
  let year = '';

  if (typeof value === 'string') {
    [year = '', month = '', day = ''] = value.split('-');
  } else if (typeof value === 'object' && value !== null) {
    ({ day = '', month = '', year = '' } = value);
  }

  return { day: padDatePart(day), month: padDatePart(month), year };
};

const passengerInitialValues = (
  userType,
  firstName = '',
  lastName = '',
  gender = 'MR',
  tcdegil = false,
  tc = '',
  passportNo = '',
  hesCode = '',
  mileNumber = '',
  dateOfBirth = { day: '', month: '', year: '' }, // default obje
  passportCountry = '',
  nationality = '',
  passportExpirationDate = { day: '', month: '', year: '' }
) => {
  return {
    firstName,
    lastName,
    gender,
    tcdegil,
    tc,
    passportNo,
    hesCode,
    mileNumber,
    userType,
    dateOfBirth: toDateParts(dateOfBirth),
    passportExpirationDate: toDateParts(passportExpirationDate),
    passportCountry,
    nationality,
  };
};

export const useCheckoutFlight = ({
  session,
  flightOfferId,
  providerType,
  flightOffersSearchForm,
  pnrDetailsObj,
  packagesCode,
  flightBookingInfo,
}) => {
  const [iframeUrl, setIframeUrl] = useState('');
  const [reservingData, setReservingData] = useState(null);
  const i18n = useI18n();

  const [
    bookingMutation,
    { loading: bookingLoading, error: bookingError },
  ] = useMutation(BOOK_FLIGHT_OFFER_MUTATION);
  const [
    reservationMutation,
    { loading: reservingLoading, error: reservingError },
  ] = useMutation(RESERVE_FLIGHT_OFFER_MUTATION);

  const booking = bookingInfo =>
    bookingMutation({
      variables: {
        flightOfferId,
        session,
        telephone: bookingInfo.isReservationPurchase
          ? pnrDetailsObj.pnrInformations.tel
          : bookingInfo.gsmNumber,
        email: bookingInfo.isReservationPurchase
          ? pnrDetailsObj.pnrInformations.email
          : bookingInfo.email,
        isReservationPurchase: bookingInfo.isReservationPurchase,
        pnr: bookingInfo.isReservationPurchase
          ? pnrDetailsObj.pnrInformations.code
          : '',
        providerType,
        passengers: bookingInfo.isReservationPurchase
          ? [
              {
                givenName: pnrDetailsObj.pnrInformations.firstName,
                surname: pnrDetailsObj.pnrInformations.lastName,
              },
            ]
          : [
              ...bookingInfo.adults.map(adult =>
                mapToBiletinizPassenger(adult, 'ADT')
              ),
              ...bookingInfo.childrens.map(child =>
                mapToBiletinizPassenger(child, 'CHD')
              ),
              ...bookingInfo.infants.map(infant =>
                mapToBiletinizPassenger(infant, 'INF')
              ),
            ],
        packagesCode: bookingInfo.packagesCode,
        useWallet: bookingInfo.useWallet,
        paymentCard: {
          cardNumber: bookingInfo.cardNumber,
          expireYear: bookingInfo.expiryDate.split('/')[1],
          expireMonth: bookingInfo.expiryDate.split('/')[0],
          cvc: bookingInfo.cvc,
          cardHolderName: bookingInfo.cardHolderName,
        },
      },
    }).then(({ data }) => {
      const apiRootUrl = process.env.NEXT_PUBLIC_API_ROOT_URL || '';
      setIframeUrl(`${apiRootUrl}/payment-checkout/${data.flightOfferBooking}`);
    });

  const reserving = reservingInfo =>
    reservationMutation({
      variables: {
        flightOfferId,
        session,
        telephone: reservingInfo.gsmNumber,
        email: reservingInfo.email,
        packagesCode: reservingInfo.packagesCode,
        passengers: [
          ...reservingInfo.adults.map(adult =>
            mapToBiletinizPassenger(adult, 'ADT')
          ),
          ...reservingInfo.childrens.map(child =>
            mapToBiletinizPassenger(child, 'CHD')
          ),
          ...reservingInfo.infants.map(infant =>
            mapToBiletinizPassenger(infant, 'INF')
          ),
        ],
      },
    }).then(({ data }) => {
      setReservingData({
        pnr: data.flightOfferReserving.PNR,
        firstPassengerSurname: data.flightOfferReserving.surname,
        firstPassengerGivenName: data.flightOfferReserving.givenName,
        flightProviderType: data.flightOfferReserving.providerType,
      });
    });

  const { travellers } = flightOffersSearchForm.cabinClassTravellers;

  const emptyInitialValues = {
    adults: range(0, travellers.adultsCount).map(() =>
      passengerInitialValues('adult')
    ),
    childrens: range(0, travellers.childrenCount).map(() =>
      passengerInitialValues('child')
    ),
    infants: range(0, travellers.infantsCount).map(() =>
      passengerInitialValues('infant')
    ),
    email: '',
    gsmNumber: '',
    cardHolderName: '',
    cardNumber: '',
    cvc: '',
    packagesCode,
    termsOfService: false,
    testAdultsAges: false,
    expiryDate: '',
    useWallet: false,
  };

  const sessionFullInitialValues = {
    adults: flightBookingInfo?.passengers?.reduce(
      (result, passengerDetails) => {
        if (
          passengerDetails.passengerTypeCode === 'ADT' ||
          passengerDetails.passengerTypeCode === 'ADULT'
        )
          result.push(
            passengerInitialValues(
              passengerTypeMapper[passengerDetails.passengerTypeCode],
              passengerDetails.givenName,
              passengerDetails.surname,
              passengerDetails.gender,
              passengerDetails.tcdegil,
              passengerDetails.tc,
              passengerDetails.passportNo,
              // TODO when we return hes code we have to format it correctly
              passengerDetails.hesCode,
              passengerDetails.mileNumber,
              passengerDetails.birthDate,
              toCountryOption(
                passengerDetails.passportCountry,
                i18n.activeLocale
              ),
              toCountryOption(passengerDetails.nationality, i18n.activeLocale),
              passengerDetails.passportExpirationDate
            )
          );
        return result;
      },
      []
    ),
    childrens: flightBookingInfo?.passengers?.reduce(
      (result, passengerDetails) => {
        if (
          passengerDetails.passengerTypeCode === 'CHD' ||
          passengerDetails.passengerTypeCode === 'CHILD'
        )
          result.push(
            passengerInitialValues(
              passengerTypeMapper[passengerDetails.passengerTypeCode],
              passengerDetails.givenName,
              passengerDetails.surname,
              passengerDetails.gender,
              passengerDetails.tcdegil,
              passengerDetails.tc,
              passengerDetails.passportNo,
              `${passengerDetails.hesCode.substr(
                0,
                4
              )}-${passengerDetails.hesCode.substr(
                4,
                4
              )}-${passengerDetails.hesCode.substr(8)}`,
              passengerDetails.mileNumber,
              passengerDetails.birthDate,
              toCountryOption(
                passengerDetails.passportCountry,
                i18n.activeLocale
              ),
              toCountryOption(passengerDetails.nationality, i18n.activeLocale),
              passengerDetails.passportExpirationDate
            )
          );
        return result;
      },
      []
    ),
    infants: flightBookingInfo?.passengers?.reduce(
      (result, passengerDetails) => {
        if (
          passengerDetails.passengerTypeCode === 'INF' ||
          passengerDetails.passengerTypeCode === 'INFANT'
        )
          result.push(
            passengerInitialValues(
              passengerTypeMapper[passengerDetails.passengerTypeCode],
              passengerDetails.givenName,
              passengerDetails.surname,
              passengerDetails.gender,
              passengerDetails.tcdegil,
              passengerDetails.tc,
              passengerDetails.passportNo,
              `${passengerDetails.hesCode.substr(
                0,
                4
              )}-${passengerDetails.hesCode.substr(
                4,
                4
              )}-${passengerDetails.hesCode.substr(8)}`,
              passengerDetails.mileNumber,
              passengerDetails.birthDate,
              toCountryOption(
                passengerDetails.passportCountry,
                i18n.activeLocale
              ),
              toCountryOption(passengerDetails.nationality, i18n.activeLocale),
              passengerDetails.passportExpirationDate
            )
          );
        return result;
      },
      []
    ),
    email: flightBookingInfo?.email,
    id: flightBookingInfo?.id,
    session: flightBookingInfo?.session,
    gsmNumber: `0 (${flightBookingInfo?.telephone.substr(
      0,
      3
    )}) ${flightBookingInfo?.telephone.substr(
      3,
      3
    )} ${flightBookingInfo?.telephone.substr(
      6,
      2
    )} ${flightBookingInfo?.telephone.substr(8)}`,
    cardHolderName: flightBookingInfo?.paymentCard.cardHolderName,
    cardNumber: `${flightBookingInfo?.paymentCard.cardNumber.substr(
      0,
      4
    )}-${flightBookingInfo?.paymentCard.cardNumber.substr(
      4,
      4
    )}-${flightBookingInfo?.paymentCard.cardNumber.substr(
      8,
      4
    )}-${flightBookingInfo?.paymentCard.cardNumber.substr(12)}`,
    packagesCode: flightBookingInfo?.packagesCode,
    expiryDate: `${flightBookingInfo?.paymentCard.expireMonth}/${flightBookingInfo?.paymentCard.expireYear}`,
    cvc: flightBookingInfo?.paymentCard.cvc,
    termsOfService: false,
  };
  let formInitialValues = emptyInitialValues;
  if (flightBookingInfo) {
    formInitialValues = sessionFullInitialValues;
  }
  return {
    bookingFlightOfferError: bookingError?.message,
    reservingFlightOfferError: reservingError?.message,
    booking,
    reserving,
    bookingLoading,
    reservingLoading,
    reservingData,
    iframeUrl,
    setIframeUrl,
    formInitialValues,
  };
};
